import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {  Col, Form, Popconfirm, Row } from "antd";
import { ActionButton } from '@hdi-seguros/web-components.action-button';
import { Grid } from '@hdi-seguros/web-components.grid';
import { Card } from "@hdi-seguros/web-components.card";

import { AuthContext, InfoContext, UIContext } from "../../shared/contexts";
import { CardSkeletons, ErrorResults, InputFactory } from "../../shared/components";
import { PageHeader } from "../../shared/components/MainLayout/components";
import AuthenticationService from "../../shared/services/authentication";
import { Combo, InputFactoryProp, Intermediaries, Intermediary, UserInformation } from "../../shared/interfaces";
import { EditOutlined, IdcardOutlined, NumberOutlined, SearchOutlined } from "@ant-design/icons";
import { COLORS } from "../../config/themes/colors";
import { _renderColumns } from "../production/InsurancePolicyDetail/utils/renderSections";
import MasterService from "../../shared/services/master";

interface IntermedaryProps {
    title: string;
    preserveIntermediary: boolean;
}


export const IntermedaryPage: FC<IntermedaryProps> = ({ title, preserveIntermediary = false }) => {
    const navigate = useNavigate();
    const gridRef = useRef()
    const authContext = useContext(AuthContext);
    const { setTitle } = useContext(InfoContext);
    const { isLoading, showError, setIsLoading, setShowError } = useContext(UIContext);
    // const { token: { borderRadiusLG } } = theme.useToken();
    const [intermediaryList, setIntermediaryList] = useState<Intermediaries | null>(null);
    const [intermediaryListFiltered, setIntermediaryListFiltered] = useState<Intermediaries | null>(null);
    const [currentPageList, setCurrentPageList] = useState<Array<Intermediary>>([]);

    const [form] = Form.useForm();
    // const [isLoadingFilters, setIsLoadingFilters] = useState(true);
    const [nivelInterCombo, setNivelInterCombo] = useState<Combo[]>([]);

    const inputsFilter: InputFactoryProp[] = useMemo(() => [
        {
            element: 'input',
            type: 'text',
            placeHolder: 'Nombre',
            icon: <SearchOutlined style={{ color: COLORS.neutrals[50] }} />,
            name: 'nombreInter',
        },
        {
            element: 'input',
            type: 'number',
            placeHolder: 'CUIT',
            icon: <IdcardOutlined style={{ color: COLORS.neutrals[50] }} />,
            name: 'cuitInter',
        },
        {
            element: 'input',
            type: 'number',
            placeHolder: 'Código Interno',
            icon: <NumberOutlined style={{ color: COLORS.neutrals[50] }} />,
            name: 'codigoInter',
        },
        {
            element: 'select',
            isMulti: true,
            allowClear: true,
            placeHolder: 'Nível',
            items: nivelInterCombo,
            name: 'nivelInter',
        },
    ], [nivelInterCombo]);

    const initialStateFilters = useMemo(() => ({
        nombreInter: '',
        cuitInter: '',
        codigoInter: '',
        nivelInter: [],
    }), []);

    const pageInitialize = async () => {
        setTitle(title);
        await getComboStates();
        form.setFieldsValue(initialStateFilters);
        const response = await fetchIntermediaries();
        setIntermediaryList(response);
        setIntermediaryListFiltered(response);
    };


    const getComboStates = async () => {
        try {
            const response = await MasterService?.getNivelIntermediarioForCombo();
            setNivelInterCombo(response.data);
        } catch (error) {}
    };

    const fetchIntermediaries = async () => {

        let intermediaries = null;

        try {
            setIsLoading(true);
            if (preserveIntermediary) {
                // Intermediario por cuit
                const response = await AuthenticationService.getIntermediariesByIntermediary();
                intermediaries = response.data as Intermediaries;
            } else {
                // Intermediario por intermediario
                const response = await AuthenticationService.getIntermediariesByUser();
                intermediaries = response.data as Intermediaries;
            }
        } catch (error) {
            setShowError(true);
        } finally {
            setIsLoading(false);
            return intermediaries;
        }
    };

    const filterIntermediaryList = () => {
        try {
            if (!intermediaryList) return;

            const { nombreInter, cuitInter, codigoInter, nivelInter } = form.getFieldsValue();
            const noFiltersApplied = !nombreInter && !cuitInter && !codigoInter && (!nivelInter || nivelInter.length === 0);

            setIsLoading(true);

            if (noFiltersApplied) {
                setIntermediaryListFiltered(intermediaryList);
            } else {
                const response = intermediaryList.intermediarios.intermediario.filter((item) => {
                    return (
                        (!nombreInter || item.nombreInter.toUpperCase().includes(nombreInter.toUpperCase())) &&
                        (!cuitInter || item.cuitInter.includes(cuitInter)) &&
                        (!codigoInter || item.codigoInter.includes(codigoInter)) &&
                        (!nivelInter || nivelInter.length === 0 || nivelInter.includes(item.nivelInter))
                    );
                });

                setIntermediaryListFiltered({
                    intermediarios: {
                        cantidad: response.length,
                        intermediario: response
                    }
                });
            }

            setIsLoading(false);
        } catch (error) {
            setShowError(true);
        }
    };


    const selectIntermediary = (intermediario: Intermediary) => {
        let userInfo = authContext.getUserInfo() as UserInformation;
        if (preserveIntermediary === false) {
            userInfo.intermediarioPrincipal = intermediario;
        }
        userInfo.intermediarioSecundario = intermediario;

        authContext.updateUserInfo(userInfo);
        navigate('/home');
    }

    const clearFilter = () => {        
        setIntermediaryListFiltered(intermediaryList);
    }

    useEffect(() => {
        if (intermediaryListFiltered ) {
            (gridRef.current as any).setPage(1);
        }
    }, [intermediaryListFiltered, setIntermediaryListFiltered]);

    useEffect(() => {
        pageInitialize();
    }, []);

    return (
        <>
            <PageHeader />
            <Row align='middle' style={{ marginBottom: 15 }}>
                <InputFactory
                    form={form}
                    items={inputsFilter}
                    onFilter={filterIntermediaryList}
                    columnSizes={{ xl: { span: 6 }, lg: { span: 12 } }}
                    // isLoading={ isLoadingFilters }
                    initialState={initialStateFilters}
                    onClear={clearFilter}
                />
            </Row>
            {
                (!intermediaryList && !isLoading) || showError
                    ? <ErrorResults title="No se pudo cargar el listado de intermediarios" />
                    : (
                        <Row style={{ width:'100%' }}>
                            <Col span={24}>
                                <Grid 
                                    list={(intermediaryListFiltered?.intermediarios?.intermediario) || []} 
                                    setState={setCurrentPageList} 
                                    name="intermediarios" 
                                    ref={gridRef}
                                >
                                <Col span={24}>
                                    {
                                        currentPageList && currentPageList?.length
                                            ? currentPageList.map((currentPageItem, index) => (
                                                <Card
                                                title=''
                                                    key={`${currentPageItem.codigoInter}-${index}`}
                                                    style={{
                                                        marginBottom: 20,
                                                        width: '100%',
                                                    }}
                                                >
                                                    <Row gutter={[10, 10]}>
                                                        {_renderColumns([
                                                            {
                                                                label: 'Nível / Cód. Interno',
                                                                value: `${currentPageItem.nivelInter} / ${currentPageItem.codigoInter}`,
                                                                isLikeTitle: true,
                                                                valueInBold: true,
                                                                columnSize: { xs: { span: 12 }, md: { span: 8 } },
                                                            },
                                                            {
                                                                label: 'Nombre',
                                                                value: currentPageItem.nombreInter,
                                                                columnSize: { xs: { span: 12 }, md: { span: 8 } },
                                                            },
                                                            {
                                                                label: 'CUIT',
                                                                value: currentPageItem.cuitInter,
                                                                columnSize: { xs: { span: 12 }, md: { span: 8 } },
                                                            },
                                                            {
                                                                label: 'Cód. Mayor Aux.',
                                                                value: currentPageItem.codMayorAux,
                                                                columnSize: { xs: { span: 12 }, md: { span: 8 } },
                                                            },
                                                            {
                                                                label: 'Nro. Mayor Aux.',
                                                                value: currentPageItem.nroMayorAux,
                                                                columnSize: { xs: { span: 12 }, md: { span: 8 } },
                                                            },
                                                            {
                                                                label: 'Acción',
                                                                columnSize: { xs: { span: 12 }, md: { span: 8 } },
                                                                value:
                                                                    <Popconfirm
                                                                        title="Confirmar selección"
                                                                        description="Por favor confirme la acción de asignación."
                                                                        okText="Confirmar"
                                                                        cancelText="Cancelar"
                                                                        onConfirm={() => selectIntermediary(currentPageItem)}
                                                                    >
                                                                        <ActionButton
                                                                            className="btn-accent-outline-barbuss"
                                                                            type="secondary"
                                                                            label="Seleccionar"
                                                                            icon={<EditOutlined />}
                                                                        />
                                                                    </Popconfirm>,
                                                            },
                                                        ])}
                                                    </Row>
                                                </Card>
                                            ))
                                            : isLoading
                                                ? (
                                                    <Row style={{ width: '100%' }} gutter={[15, 15]}>
                                                        <CardSkeletons />
                                                    </Row>
                                                ) : <ErrorResults title="No fueron encontrados intermediarios" />
                                    }
                                </Col>
                                </Grid>
                            </Col>
                        </Row>
                    )
            }
        </>
    )
}
